import { first } from 'rxjs/operators';
import { TagService, Tag } from '@app/_components/tag';
import { OrderProductService } from '@app/_components/order-product';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TagComponent } from '../tag/tag.component';
import { TagCategoryManageDialogComponent } from '../tag-category-manage-dialog/tag-category-manage-dialog.component';
import { OpCoverageDialogComponent } from '../op-coverage-dialog/op-coverage-dialog.component';
import { Component, Input, OnInit } from '@angular/core';
import { AlertService } from '@app/_services';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';

@Component({
    selector: 'app-tag-list',
    templateUrl: './tag-list.component.html',
    styleUrls: ['./tag-list.component.css'],
    providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
    // make column heading sticky
    styles: [`
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
            position: -webkit-sticky;
            position: sticky;
            top: 0px;
        }

        @media screen and (max-width: 64em) {
            :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
                top: 99px;
            }
        }
`],
    standalone: false
})
export class TagListComponent implements OnInit {
	visibilities: any;
	tagTypes: any;
	constructor(
		private tagService: TagService,
		private orderProductService: OrderProductService,
		private alertService: AlertService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private dialog: MatDialog,
	) {
		this.visibilities = tagService.visibilities;
		this.tagTypes = tagService.tagTypes;
	}

	/**
	 * Map of orderProductId → name. Loaded once on init and used to display the
	 * OP name alongside its id in the "OP Trigger" column. Built from the same
	 * /find-all endpoint the Tag dialog uses, so it covers the entire catalog.
	 */
	private orderProductNamesById: Record<number, string> = {};



	// data
	@Input() showFilter = true;
	@Input() showPagination = true;
	@Input() outlineNewButton = true;
	tags: any;
	tag: Tag;

	// Sync-missing-Breezeway state. Count is the number of tagType=8 rows with
	// breezewayTagId == null in the CURRENT page; it drives both the warning
	// badges on individual rows and the "Sync N to Breezeway" button visibility.
	// Recomputed in loadLazy after every fetch.
	missingBreezewayCount = 0;
	syncingBreezeway = false;

	// paging
	first = 0;
	rows = 50;

	// list
	datasource: any;

	recordTotal: number;
	lazyLoadEvent: LazyLoadEvent;
	loading = true;
	multiSortMeta = [];

	// forms
	formLoading = false;

	statuses: any;

	columns: any[] = [
		{ field: 'tagId', header: 'Id', width: 'auto' },
		{ field: 'name', header: 'Name', width: 'auto' },
		{ field: 'description', header: 'Description', width: 'auto' },
		{ field: 'tagGroupCategory', header: 'Category', width: 'auto' },
		{ field: 'tagCategoryColor', header: 'Color', width: '70px' },          // R3 — swatch
		{ field: 'tagType', header: 'Type', width: 'auto' },
		{ field: 'visibility', header: 'Visibility', width: 'auto' },
		{ field: 'breezewayTagId', header: 'Breezeway Id', width: 'auto' },     // R3 — Breezeway link
		{ field: 'orderProductId', header: 'OP Trigger', width: 'auto' },       // R1 (Approach A) — direct OP→tag link
		{ field: 'ownedByUserVO.username', header: 'Owner', width: 'auto' },    // R3 — owner display
	];

	ngOnInit(): void {
		try {
			// default sort of list
			this.multiSortMeta = [];
			this.multiSortMeta.push({ field: 'createDate', order: 0 });
			// Load the OP catalog once so the "OP Trigger" column can show names
			// alongside the orderProductId stored on each tag.
			this.loadOrderProductNames();
		}
		catch (error) {
			console.log('Error: ' + error);
		}
	}

	private loadOrderProductNames(): void {
		this.orderProductService.findAllOrderProducts().pipe(first()).subscribe(
			(res: any) => {
				const list = res?.data?.orderProducts
					|| res?.data?.orderProductList
					|| res?.data?.orderProductVOList
					|| res?.data?.list
					|| [];
				const map: Record<number, string> = {};
				for (const op of list) {
					if (op && op.orderProductId != null) {
						map[op.orderProductId] = op.name || '(unnamed)';
					}
				}
				this.orderProductNamesById = map;
			},
			(err: any) => {
				console.log('loadOrderProductNames FAIL: ' + JSON.stringify(err));
			}
		);
	}

	/** Used by the OP Trigger column. Returns empty string when not loaded yet or unknown id. */
	getOrderProductName(opId: number | null | undefined): string {
		if (opId == null) return '';
		return this.orderProductNamesById[opId] || '';
	}

	/*********
	 * Delete Button — R5 cascade-aware confirmation.
	 *
	 * Flow:
	 *   1. GET /tags/{id}/delete-preview to fetch impact counts.
	 *   2. If canDelete = false (children exist), show error toast and abort.
	 *   3. Otherwise show confirm dialog with the impact summary.
	 */
	onRowDelete(event: any) {
		console.log('onRowDelete: ' + JSON.stringify(event));
		this.tagService.deletePreview(event.tagId).pipe(first()).subscribe(
			(res: any) => {
				const preview = res?.data?.preview;
				if (!preview) {
					// Backend missing the new endpoint? Fall back to a plain confirmation.
					this.simpleConfirmDelete(event);
					return;
				}
				if (preview.canDelete === false) {
					this.messageService.add({
						severity: 'error',
						summary: 'Cannot delete',
						detail: `Tag '${ preview.tagName }' has ${ preview.childrenCount } child tag(s). Delete or re-parent them first.`,
						life: 6000
					});
					return;
				}
				const parts: string[] = [];
				if (preview.reservationCount > 0)  parts.push(`on ${ preview.reservationCount } reservation(s)`);
				if (preview.folioMappingCount > 0) parts.push(`mapped to ${ preview.folioMappingCount } folio rule(s)`);
				const impact = parts.length ? ` It is currently ${ parts.join(' and ') }.` : '';
				const breezeway = preview.breezewayTagId
					? ` Breezeway tag id ${ preview.breezewayTagId } will also be removed.`
					: '';
				this.confirmationService.confirm({
					message: `Delete tag '${ preview.tagName }'?${ impact }${ breezeway }`,
					header: 'Confirm Delete',
					icon: 'pi pi-exclamation-triangle',
					accept: () => { this.formDelete(event); },
					reject: () => {
						this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
					}
				});
			},
			(err: any) => {
				console.log('deletePreview FAIL — falling back to simple confirm', err);
				this.simpleConfirmDelete(event);
			}
		);
	}

	private simpleConfirmDelete(event: any) {
		this.confirmationService.confirm({
			message: 'Are you sure you want to delete Id# ' + event.tagId + '?',
			header: 'Confirm',
			icon: 'pi pi-exclamation-triangle',
			accept: () => { this.formDelete(event); },
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
			}
		});
	}


	/*********
	 * Lazy Load All Data
	 *
	 */
	loadLazy(event: LazyLoadEvent) {
		try {
			this.loading = true;
			this.lazyLoadEvent = event;

			console.log(
				' event.first:' + event.first
				+ '  |  event.rows:' + event.rows
				+ '  |  event.sortField:' + event.sortField
				+ '  |  event.sortOrder:' + event.sortOrder
				+ '  |  event.multiSortMeta:' + JSON.stringify(event.multiSortMeta)
				+ '  |  event.globalFilter:' + event.globalFilter
				+ '  |  event.filters:' + JSON.stringify(event.filters)
			);

			/*
			*
			* Default Sort
			*/
			// https://stackoverflow.com/questions/679915/how-do-i-tag-for-an-empty-javascript-object
			// if we are NOT filtering and we are NOT sorting, apply a default sort
			if (Object.keys(event.filters).length === 0 && !event.multiSortMeta) {
				event.multiSortMeta = [];
				event.multiSortMeta.push({ field: 'createDate', order: -1 });
			}

			const pageNumber = Math.round(event.first / event.rows);
			this.tagService.findByFilter(
				pageNumber, event.rows,
				event.sortField,
				event.sortOrder,
				event.multiSortMeta,
				event.globalFilter,
				event.filters).pipe(first()).subscribe(

					(res: any) => {
						this.datasource = res.data.reviews;
						this.recordTotal = res.page.recordTotal;
						this.tags = res.data.tags;
						// Recompute the "missing Breezeway" badge / button state for the
						// page just loaded. Reservation tags only — other tagTypes don't
						// have Breezeway counterparts.
						this.missingBreezewayCount = (this.tags || []).filter((t: any) => this.isMissingBreezewayId(t)).length;
						console.log(JSON.stringify(res.page));
					},
					(err: any) => {
						console.log('reviews list error!: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.loading = false; }
				);

			if (this.datasource) {
				this.tags = this.datasource.slice(event.first, (event.first + event.rows));
			}
		} catch (error) {
			console.log('loadLazy(' + event + ')' + error);
		}
	}

	/*********
	 * onRowSelect()
	 * Used to handle row click (to edit, link, etc)
	 */
	onRowSelect(event: any) {
		console.log('onRowSelect: ' + JSON.stringify(event));
		this.onOpenFormEdit(event.data);
	}

	// handled in rate-bucket-form
	formAdd() { }


	/*********
	 * formUpdate()
	 * Used for row editing
	 */
	formUpdate(tag: Tag) {
		try {
			this.formLoading = true;
			this.tagService.update(tag)
				.subscribe(
					(res: any) => {
						console.log('Rate-Bucket-Update SUCCESS: ' + JSON.stringify(res));
						this.tags.map((tag, i) => {
							if (tag.tagId === res.data.tag.tagId) {
								this.tags[i] = res.data.tag;
							}
						});
						// this.alertService.success(res, true);
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
					},
					(err: any) => {
						console.log('Rate-Bucket-Update FAIL: ' + JSON.stringify(err));
						this.messageService.add({ severity: 'error', summary: JSON.stringify(err), life: 3000 });
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

	/*********
	 * formDelete()
	 * Used for row editing
	 */
	formDelete(tag: any) {
		try {
			// stop here if form is invalid
			console.log('Delete Submitted: ' + JSON.stringify(tag));

			this.formLoading = true;
			this.tagService.delete(tag.tagId)
				.subscribe(
					(res: any) => {
						console.log('Rate-Bucket-Delete SUCCESS: ' + JSON.stringify(res));
						this.tags.splice(this.tags.indexOf(tag), 1);
						this.recordTotal = this.tags.length;
						this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Tag ' + tag.tagId + ' Deleted', life: 3000 });
					},
					(err: any) => {
						console.log('Rate-Bucket-Delete  FAIL: ' + JSON.stringify(err));
						this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: 'Tag ' + tag.tagId + ' could not be deleted.  Be sure there are no assigned properties to this bucket.', life: 3000 });
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

	/*********
		 * Add New Button
		 *
		 */
	/**
	 * Open the Add Tag dialog. Optional {@code prefilledOrderProductId} pre-selects
	 * the OP trigger and forces tagType=8 — used by the OP-coverage flow so the
	 * admin lands one click away from creating a tag for the gap they just spotted.
	 */
	onOpenFormAdd(prefilledOrderProductId?: number) {

		const dialogConfig = new MatDialogConfig();

		// settings
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;

		// Pass a partial tag (no tagId) when we have a prefill. The Tag dialog's
		// applyEditPatchIfNeeded patches whatever fields are set; it shows the
		// "Add" button (not "Update") because tagId is undefined.
		const partialTag = prefilledOrderProductId != null
			? { orderProductId: prefilledOrderProductId, tagType: 8 }
			: undefined;

		dialogConfig.data = {
			title: 'Add Tag',
			tag: partialTag
		};

		// open the popup and get a reference to it called dialogRef
		const dialogRef = this.dialog.open(TagComponent, dialogConfig);

		// to receive the added element, subscribe to the eventEmitter we created 'doAdd' in the element to handle adding
		dialogRef.componentInstance.doAdd.subscribe(
			data => {
				console.log('Dialog output:', data);

				// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
				this.addTagCB(data);

				// close the dialogBox
				dialogRef.close();
			});
	}

	/**
	 * Open the OP coverage audit dialog. Lists every order product that has no
	 * tag wired. When the admin clicks "Wire a tag" on one, the dialog emits
	 * the OP id and we open the standard Add Tag dialog with that OP pre-filled,
	 * giving them a one-click path from gap spotted → tag created.
	 */
	onOpenOpCoverage() {
		const cfg = new MatDialogConfig();
		cfg.disableClose = false;
		cfg.autoFocus = true;
		cfg.hasBackdrop = true;
		cfg.data = {};

		const ref = this.dialog.open(OpCoverageDialogComponent, cfg);
		ref.componentInstance.wireTagFor.subscribe((opId: number) => {
			// Coverage dialog has already self-closed by the time this fires.
			this.onOpenFormAdd(opId);
		});
	}


	/*********
		* Edit Button
		*
		*/
	onOpenFormEdit(tag: Tag) {

		const dialogConfig = new MatDialogConfig();

		// settings
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;

		// pass data, ie rateBuicket: this.shortTermRentalId,
		dialogConfig.data = {
			tag: tag,
			title: 'Edit Tag'
		};

		// open the popup and get a reference to it called dialogRef
		const dialogRef = this.dialog.open(TagComponent, dialogConfig);

		// to receive the added element, subscribe to the eventEmitter we created 'doUpdate' in the element to handle updating the list
		dialogRef.componentInstance.doUpdate.subscribe(
			data => {
				console.log('Dialog output:', data);

				// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
				this.updateTagCB(data);

				// close the dialogBox
				dialogRef.close();
			});

		dialogRef.componentInstance.doDelete.subscribe(
			data => {
				console.log('Dialog output:', data);

				// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
				this.deleteTagCB(data);

				// close the dialogBox
				dialogRef.close();
			});
	}


	/**
	   *
	   * Dialog from Form Callbacks
	   * To edit the list show add/edit/delete in real time from the popup form
	   *
	   */
	addTagCB(tag: Tag) {
		console.log('Adding Tag: ' + JSON.stringify(tag));
		// if we don't have a tags array yet...
		if (!this.tags) {
			this.tags = [];
		}
		// unshift adds to the front of the array (), push adds to the back
		this.tags.unshift(tag);

		// no idea what this is but it is required to update the array
		this.tags = [...this.tags];

		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Tag ' + tag.tagId + ' Added', life: 3000 });
	}

	updateTagCB(tag: Tag) {
		console.log('Updating Tag: ' + JSON.stringify(tag));
		let itemIndex = this.tags.findIndex((item: any) => item.tagId === tag.tagId);
		this.tags[itemIndex] = tag;

		// no idea what this is but it is required to update the array
		this.tags = [...this.tags];

		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Tag ' + tag.tagId + ' Updated', life: 3000 });
	}

	deleteTagCB(tag: Tag) {
		console.log('Removing Tag: ' + tag);
		this.tags.splice(this.tags.indexOf(tag), 1);
		this.recordTotal = this.tags.length;

		// no idea what this is but it is required to update the array
		this.tags = [...this.tags];

		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Tag ' + tag.tagId + ' Deleted', life: 3000 });
	}

	/**********
	 *
	 * PAGINATOR FUNCTIONS
	 */
	next() { this.first = this.first + this.rows; }
	prev() { this.first = this.first - this.rows; }
	reset() { this.first = 0; }
	isLastPage(): boolean { return this.tags ? this.first === (this.tags.length - this.rows) : true; }
	isFirstPage(): boolean { return this.tags ? this.first === 0 : true; }

	get Statuses() {
		return this.statuses;
	}

	getVisibility(tag: any) {
		if (tag?.visibility) {
			const visibility = this.visibilities.find((e: any) => e.value == tag.visibility);
			return visibility?.name || '';
		}
		else {
			return '';
		}
	}
	getTagType(tag: any) {
		if (tag?.tagType) {
			const tagType = this.tagTypes.find((e: any) => e.value == tag.tagType);
			return tagType?.name || '';
		}
		else {
			return '';
		}
	}

	/**
	 * R3 — visual flag for tag-type=8 (reservation tags) that lack a breezewayTagId.
	 * Those tags can't be pushed to Breezeway until an admin assigns the id, so we
	 * tint the row and show a warning icon.
	 */
	isMissingBreezewayId(tag: any): boolean {
		return tag && tag.tagType === 8 && (tag.breezewayTagId == null);
	}

	/**
	 * Sweep all visible tagType=8 rows that lack a breezewayTagId, send their
	 * tagIds to the backend's batch sync endpoint, and refresh the table on
	 * completion. Selective-by-tagId mode means we never touch tags off the
	 * current page (e.g. Marriott tier or Return Guest tags that may not yet
	 * be intended to flow to Breezeway).
	 */
	onSyncMissingBreezeway() {
		const tagIds = (this.tags || [])
			.filter((t: any) => this.isMissingBreezewayId(t))
			.map((t: any) => t.tagId);
		if (tagIds.length === 0) return;
		this.confirmationService.confirm({
			message: `Create Breezeway tags for ${ tagIds.length } reservation tag(s) missing a Breezeway id? This will call Breezeway's create-tag API for each.`,
			header: 'Sync to Breezeway',
			icon: 'pi pi-cloud-upload',
			accept: () => {
				this.syncingBreezeway = true;
				this.tagService.syncMissingBreezewayTags(tagIds).pipe(first()).subscribe(
					(res: any) => {
						const summary = res?.data?.syncSummary;
						const created    = summary?.created    ?? 0;
						const skipped    = summary?.skipped    ?? 0;
						const failed     = summary?.failed     ?? 0;
						const ineligible = summary?.ineligible ?? 0;
						const sev = failed > 0 ? 'warn' : 'success';
						const parts = [`${ created } created`, `${ skipped } skipped`, `${ failed } failed`];
						if (ineligible > 0) parts.push(`${ ineligible } ineligible`);
						this.messageService.add({
							severity: sev,
							summary: 'Breezeway sync complete',
							detail: parts.join(', ') + '.',
							life: 6000
						});
						// Refresh the page so newly-assigned breezewayTagIds show up.
						if (this.lazyLoadEvent) this.loadLazy(this.lazyLoadEvent);
					},
					(err: any) => {
						console.log('syncMissingBreezewayTags FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.syncingBreezeway = false; }
				);
			},
			reject: () => { /* no-op */ }
		});
	}

	reloadPage() {
		window.location.reload();
	}

	showPaginator()
	{
		return true;
	}

	/*********
	 * Manage Categories Button
	 */
	onOpenManageCategories() {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.width = '600px';

		const dialogRef = this.dialog.open(TagCategoryManageDialogComponent, dialogConfig);

		dialogRef.afterClosed().subscribe(result => {
			// If any category was renamed/cleared, refresh the tag list so categories shown there are up to date
			if (result && result.changed) {
				this.reloadPage();
			}
		});
	}
}
